import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import leadService from '../services/leadService';
import {
  getLeadPopupSuppressionCookie,
  isLeadSubscribedCookieSet,
  setLeadPopupClosedCookie,
  setLeadPopupDontShowCookie,
  setLeadSubscribedCookie
} from '../utils/leadPopupCookies';

const SESSION_TRIGGERED_KEY = 'lead_popup_triggered_session_v1';

// Paths where the popup must never appear
const SUPPRESSED_PATHS = [
  '/login',
  '/signup',
  '/auth',
  '/checkout',
  '/booking',
  '/payment',
  '/admin',
  '/jetsetter',
  '/airliner',
  '/airliner/auth',
  '/forgot-password',
  '/reset-password',
  '/email-verify',
  '/email-Verify',
  '/complete-profile',
  '/setup-2fa',
  '/2fa-verify',
  '/twofauth',
  '/password',
  '/flight-booking-flow',
  '/add-passengers',
  '/payment',
  '/payment-callback',
  '/booking-success',
  '/booking-confirmation',
  '/check-in',
  '/invoice',
  '/welcome'
];

/**
 * Check if the current path is in the suppressed list
 */
function isRestrictedPath(pathname) {
  return SUPPRESSED_PATHS.some((path) => pathname.startsWith(path));
}

/**
 * useLeadPopupTrigger
 *
 * Manages all three trigger conditions:
 *   1. Time-based: 30 seconds after page load
 *   2. Scroll-based: user scrolls 50% of the page
 *   3. Exit intent: mouse leaves the viewport from the top
 *
 * Respects suppression rules and restricted paths.
 * Each trigger fires at most once per session.
 *
 * @returns {{ isVisible: boolean, onClose: () => void, onSubscribed: () => void }}
 */
function useLeadPopupTrigger() {
  const [isVisible, setIsVisible] = useState(false);
  const [triggerType, setTriggerType] = useState(null);
  const [isServerSuppressed, setIsServerSuppressed] = useState(false);
  const [isStatusLoaded, setIsStatusLoaded] = useState(false);
  const location = useLocation();

  // Track whether we've already triggered this session
  const hasTriggeredRef = useRef(false);
  const isOpeningRef = useRef(false);

  // Refs for cleanup
  const timerRef = useRef(null);
  const scrollListenerRef = useRef(null);
  const exitListenerRef = useRef(null);

  /**
   * Clean up all active listeners and timers
   */
  const cleanupAll = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (scrollListenerRef.current) {
      window.removeEventListener('scroll', scrollListenerRef.current);
      scrollListenerRef.current = null;
    }
    if (exitListenerRef.current) {
      document.removeEventListener('mouseleave', exitListenerRef.current);
      exitListenerRef.current = null;
    }
  }, []);

  /**
   * Show the popup — only if not already shown and not suppressed
   */
  const triggerPopup = useCallback((source) => {
    if (hasTriggeredRef.current || isOpeningRef.current) return;
    if (!isStatusLoaded) return;
    if (isLeadSubscribedCookieSet()) return;
    if (getLeadPopupSuppressionCookie()) return;
    if (isServerSuppressed) return;
    if (isRestrictedPath(location.pathname)) return;

    isOpeningRef.current = true;
    hasTriggeredRef.current = true;
    sessionStorage.setItem(SESSION_TRIGGERED_KEY, 'true');
    setTriggerType(source);
    setIsVisible(true);
    cleanupAll();
    leadService.trackPopupEvent({
      eventName: 'popup_shown',
      route: location.pathname,
      triggerType: source
    }).catch(() => {});
  }, [cleanupAll, isStatusLoaded, location.pathname, isServerSuppressed]);

  // Fetch server suppression (fingerprint + authenticated subscription mirror)
  useEffect(() => {
    let cancelled = false;
    setIsStatusLoaded(false);
    (async () => {
      try {
        const result = await leadService.getPopupStatus(location.pathname);
        if (cancelled) return;
        setIsServerSuppressed(!!result?.suppressed);
      } catch {
        if (cancelled) return;
        setIsServerSuppressed(false);
      } finally {
        if (!cancelled) setIsStatusLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    // Don't set up triggers on restricted paths or if already suppressed
    if (isRestrictedPath(location.pathname)) return;
    if (!isStatusLoaded) return;
    if (isLeadSubscribedCookieSet()) return;
    if (getLeadPopupSuppressionCookie()) return;
    if (isServerSuppressed) return;
    if (sessionStorage.getItem(SESSION_TRIGGERED_KEY) === 'true') {
      hasTriggeredRef.current = true;
      return;
    }

    // --- Trigger 1: Time-based (30 seconds) ---
    timerRef.current = setTimeout(() => {
      triggerPopup('timer');
    }, 30000);

    // --- Trigger 2: Scroll-based (50% of page) ---
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (docHeight <= 0) return;
      const scrollPercent = (scrollTop / docHeight) * 100;
      if (scrollPercent >= 50) {
        triggerPopup('scroll');
      }
    };
    scrollListenerRef.current = handleScroll;
    window.addEventListener('scroll', handleScroll, { passive: true });

    // --- Trigger 3: Exit intent (mouse leaves viewport from top) ---
    const handleMouseLeave = (e) => {
      // Only trigger when mouse exits from the top of the viewport
      if (e.clientY <= 0) {
        triggerPopup('exit_intent');
      }
    };
    exitListenerRef.current = handleMouseLeave;
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      cleanupAll();
    };
  }, [location.pathname, triggerPopup, cleanupAll, isServerSuppressed, isStatusLoaded]);

  /**
   * Called when user clicks the close (X) button
   * Sets 7-day suppression cookie
   */
  const handleClose = useCallback(() => {
    setIsVisible(false);
    setLeadPopupClosedCookie();
    leadService.suppressPopup({
      reason: 'closed',
      route: location.pathname,
      triggerType
    }).catch(() => {});
    cleanupAll();
  }, [cleanupAll, location.pathname, triggerType]);

  const handleDontShowAgain = useCallback(() => {
    setIsVisible(false);
    setLeadPopupDontShowCookie();
    leadService.suppressPopup({
      reason: 'dont_show',
      route: location.pathname,
      triggerType
    }).catch(() => {});
    cleanupAll();
  }, [cleanupAll, location.pathname, triggerType]);

  /**
   * Called after successful subscription
   * Sets permanent suppression flag
   */
  const handleSubscribed = useCallback(() => {
    setIsVisible(false);
    setLeadSubscribedCookie();
    cleanupAll();
  }, [cleanupAll]);

  return {
    isVisible,
    triggerType,
    onClose: handleClose,
    onDontShowAgain: handleDontShowAgain,
    onSubscribed: handleSubscribed
  };
}

export default useLeadPopupTrigger;
